import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Linkedin, Mail, MapPin } from 'lucide-react';

export function ConnectSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section 
      id="connect"
      data-testid="connect-section"
      ref={ref}
      className="relative py-32 px-6 bg-card/30"
    >
      <div className="max-w-5xl mx-auto">
        {/* Two-column layout: photo left, info right */}
        <div className="flex flex-col md:flex-row items-center md:items-start gap-12 md:gap-28">

          {/* Left: Photo */}
          <motion.div
            initial={{ opacity: 0, scale: 0.85 }}
            animate={isInView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.8, ease: 'easeOut' }}
            className="flex-shrink-0 md:-ml-6"
          >
            <div className="w-56 h-56 md:w-72 md:h-72 rounded-full overflow-hidden ring-4 ring-vedic-gold/30 shadow-[0_0_40px_rgba(212,175,55,0.15)]">
              <img
                src="/assets/sachin-photo.png"
                alt="Sachin Kothamasa"
                className="w-full h-full object-cover"
              />
            </div>
          </motion.div>

          {/* Right: Info */}
          <div className="flex flex-col items-center md:items-start text-center md:text-left">
            {/* Heading */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="mb-10"
            >
              <span className="text-xs font-bold uppercase tracking-[0.3em] text-temple-red font-body block mb-4">
                Let's Build Something
              </span>
              <h2 className="font-heading text-5xl md:text-7xl font-bold italic text-vedic-gold text-glow">
                Connect
              </h2>
            </motion.div>

            {/* Credentials */}
            <motion.div
              initial={{ opacity: 0, y: 25 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.25 }}
              className="mb-10"
            >
              <div className="flex items-center gap-5 text-lg font-body text-muted-foreground">
                <div className="glass px-5 py-2.5">
                  <span className="text-vedic-gold font-heading font-bold">ACCA</span>
                </div>
                <div className="glass px-5 py-2.5">
                  <span className="text-deep-ochre font-heading font-bold">CISA</span>
                </div>
                <div className="glass px-5 py-2.5">
                  <span className="text-temple-red font-heading font-bold">CSOE</span>
                </div>
              </div>
            </motion.div>

            {/* Contact Links */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="flex flex-col sm:flex-row gap-5 mb-8"
            >
              <a
                href="mailto:sachin.kothamasa@gmail.com"
                data-testid="connect-email"
                className="group flex items-center gap-4 glass border border-white/10 hover:border-vedic-gold/50 
                  px-6 py-5 transition-all duration-300 hover:shadow-[0_0_30px_rgba(212,175,55,0.2)]"
              >
                <div className="w-12 h-12 bg-vedic-gold/10 rounded-full flex items-center justify-center
                  group-hover:scale-110 transition-transform">
                  <Mail className="w-5 h-5 text-vedic-gold" />
                </div>
                <div className="text-left">
                  <span className="text-xs uppercase tracking-widest text-muted-foreground font-body block mb-0.5">
                    Email
                  </span>
                  <span className="text-foreground font-body text-sm group-hover:text-vedic-gold transition-colors">
                    sachin.kothamasa@gmail.com
                  </span>
                </div>
              </a>

              <a
                href="https://www.linkedin.com/in/sachin-k-9806b2189/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="connect-linkedin"
                className="group flex items-center gap-4 glass border border-white/10 hover:border-deep-ochre/50 
                  px-6 py-5 transition-all duration-300 hover:shadow-[0_0_30px_rgba(204,119,34,0.2)]"
              >
                <div className="w-12 h-12 bg-deep-ochre/10 rounded-full flex items-center justify-center
                  group-hover:scale-110 transition-transform">
                  <Linkedin className="w-5 h-5 text-deep-ochre" />
                </div>
                <div className="text-left">
                  <span className="text-xs uppercase tracking-widest text-muted-foreground font-body block mb-0.5">
                    LinkedIn
                  </span>
                  <span className="text-foreground font-body text-sm group-hover:text-deep-ochre transition-colors">
                    Connect with me
                  </span>
                </div>
              </a>
            </motion.div>

            {/* Location */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={isInView ? { opacity: 1 } : {}}
              transition={{ duration: 0.6, delay: 0.55 }}
              className="flex items-center gap-2.5 text-muted-foreground"
            >
              <MapPin className="w-4 h-4 text-temple-red" />
              <span className="font-body text-sm">Bengaluru, India</span>
            </motion.div>
          </div>
        </div>

        {/* Decorative quote — full-width below */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ duration: 0.8, delay: 0.7 }}
          className="mt-20 pt-12 border-t border-white/10 text-center"
        >
          <blockquote className="font-heading text-2xl md:text-3xl italic text-muted-foreground/60">
            "Curiosity meets structure."
          </blockquote>
        </motion.div>
      </div>

      {/* Footer */}
      <div className="mt-20 pt-8 border-t border-white/10 text-center">
        <p className="text-xs text-muted-foreground/50 font-body uppercase tracking-widest">
          © 2026 Sachin K · Built with curiosity and code
        </p>
      </div>
    </section>
  );
}
